import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  PaperPlaneRight, Robot, X, Trash, Lightning, Camera, Paperclip, Minus, ArrowSquareOut,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const IDLE_GREETING = "Halo! Apakah ada yang bisa saya kerjakan hari ini?";
const IDLE_MS = 30000; // 30 detik

// Blok aksi terstruktur yang disisipkan AI di akhir balasan: <<ACTION {...}>>
const ACTION_RE = /<<ACTION\s+(\{[\s\S]*?\})\s*>>/g;
const ACTION_ROUTES = {
  sales_quotation: "/app/sales",
  sales_order: "/app/sales",
  sales_invoice: "/app/sales",
  purchase_order: "/app/purchases",
  purchase_invoice: "/app/purchases",
};
const ACTION_LABELS = {
  sales_quotation: "Buat Penawaran",
  sales_order: "Buat Pesanan Penjualan",
  sales_invoice: "Buat Faktur Penjualan",
  purchase_order: "Buat Pesanan Pembelian",
  purchase_invoice: "Buat Faktur Pembelian",
};

function extractActions(content) {
  const actions = [];
  const text = String(content || "").replace(ACTION_RE, (_, j) => {
    try {
      const a = JSON.parse(j);
      if (a?.action && ACTION_ROUTES[a.action]) actions.push(a);
    } catch (_) { /* abaikan blok rusak */ }
    return "";
  }).trim();
  return { text, actions };
}

export default function FloatingAIChat() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [autoEntry, setAutoEntry] = useState(false);
  const [autoPreview, setAutoPreview] = useState(null);
  const [lastImage, setLastImage] = useState(null);
  const [modelLabel, setModelLabel] = useState("Claude Sonnet 5.5");
  const [currentModel, setCurrentModel] = useState({ provider: "anthropic", model: "claude-sonnet-5-5" });
  const [unread, setUnread] = useState(0);
  const [greeted, setGreeted] = useState(false);
  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);
  const docInputRef = useRef(null);
  const idleTimerRef = useRef(null);

  // Load current model from backend sekali di awal (fallback graceful bila 403)
  useEffect(() => {
    api.get("/ai/models").then((r) => {
      if (r.data?.current) setCurrentModel(r.data.current);
      const label = r.data?.models?.find?.((m) => m.provider === r.data.current?.provider && m.model === r.data.current?.model)?.label;
      if (label) setModelLabel(label);
    }).catch(() => {});
  }, []);

  // Auto-scroll
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  // Idle greeting — setelah IDLE_MS tanpa interaksi, AI menyapa pengguna.
  const resetIdleTimer = () => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      // Hanya greet sekali per session; tampilkan juga sebagai badge unread bila widget tertutup.
      if (greeted) return;
      setGreeted(true);
      const greetMsg = { role: "assistant", content: IDLE_GREETING, id: `greet-${Date.now()}` };
      setMessages((m) => (m.length === 0 ? [greetMsg] : m));
      if (!open) setUnread((u) => u + 1);
    }, IDLE_MS);
  };

  useEffect(() => {
    resetIdleTimer();
    const events = ["mousemove", "keydown", "click", "touchstart"];
    events.forEach((e) => window.addEventListener(e, resetIdleTimer, { passive: true }));
    return () => {
      events.forEach((e) => window.removeEventListener(e, resetIdleTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, greeted]);

  // Buka widget → bersihkan unread
  useEffect(() => { if (open) setUnread(0); }, [open]);

  const send = async () => {
    if (!input.trim() || loading) return;

    if (autoEntry) {
      const msg = input; setInput(""); setLoading(true);
      setMessages((m) => [...m, { role: "user", content: msg, id: `tmp-${Date.now()}` }]);
      try {
        const r = await api.post("/ai/auto-entry", { message: msg, execute: false });
        setAutoPreview(r.data);
        setMessages((m) => [...m, { role: "assistant", content: `⚡ ${r.data.confirmation_message || r.data.action || "Tidak jelas"}${r.data.action === "unknown" ? "" : "\n\nKlik ✓ Konfirmasi untuk eksekusi."}`, id: `auto-${Date.now()}` }]);
      } catch (e) { toast.error(e?.response?.data?.detail || "Gagal parse"); }
      finally { setLoading(false); }
      return;
    }

    const userMsg = { role: "user", content: input, id: `tmp-${Date.now()}` };
    setMessages((m) => [...m, userMsg, { role: "assistant", content: "", id: "streaming", streaming: true }]);
    const msg = input;
    setInput("");
    setLoading(true);

    try {
      const token = localStorage.getItem("nusa_token");
      const resp = await fetch(`${API}/ai/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: msg, session_id: sessionId, provider: currentModel.provider, model: currentModel.model }),
      });
      if (resp.status === 429) {
        const err = await resp.json();
        toast.error(err.detail || "Kuota habis. Upgrade paket.");
        setMessages((m) => m.filter((x) => x.id !== "streaming"));
        setLoading(false);
        return;
      }
      if (!resp.ok || !resp.body) throw new Error("Stream error");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      let sess = sessionId;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n\n").filter((l) => l.startsWith("data: "));
        for (const line of lines) {
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === "delta") {
              acc += data.content;
              if (!sess && data.session_id) sess = data.session_id;
              setMessages((m) => m.map((x) => (x.id === "streaming" ? { ...x, content: acc } : x)));
            } else if (data.type === "done") {
              sess = data.session_id;
            } else if (data.type === "error") {
              toast.error(data.error);
            }
          } catch (_) { /* skip */ }
        }
      }
      if (!sessionId && sess) setSessionId(sess);
      setMessages((m) => m.map((x) => (x.id === "streaming" ? { ...x, streaming: false, id: `assist-${Date.now()}` } : x)));
      window.dispatchEvent(new Event("nusa:quota-updated"));
    } catch (err) {
      toast.error("Gagal mengirim pesan ke AI");
      setMessages((m) => m.filter((x) => x.id !== "streaming"));
    } finally {
      setLoading(false);
    }
  };

  const executeAutoEntry = async () => {
    if (!autoPreview) return;
    setLoading(true);
    try {
      const lastUser = [...messages].reverse().find((m) => m.role === "user");
      const r = await api.post("/ai/auto-entry", {
        message: lastUser?.content || "",
        execute: true,
        image_base64: lastImage || undefined,
      });
      if (r.data.executed) {
        toast.success("✓ Tersimpan");
        setMessages((m) => [...m, { role: "assistant", content: `✓ Transaksi tersimpan.\n\n${JSON.stringify(r.data.created, null, 2)}`, id: `done-${Date.now()}` }]);
      } else {
        toast.error(r.data.error || "Gagal eksekusi");
      }
      setAutoPreview(null);
      setLastImage(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setLoading(false); }
  };

  const scanReceipt = async (file) => {
    if (!file) return;
    const okType = ["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(file.type);
    if (!okType) { toast.error("Format tidak didukung. JPG/PNG/WEBP."); return; }
    if (file.size > 6 * 1024 * 1024) { toast.error("Ukuran maks 6MB"); return; }
    setLoading(true);
    const b64 = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result).split(",", 2)[1]);
      r.onerror = reject;
      r.readAsDataURL(file);
    });
    const previewUrl = URL.createObjectURL(file);
    setMessages((m) => [...m, { role: "user", id: `scan-${Date.now()}`, content: `📷 Scan bon: ${file.name}`, image_url: previewUrl }]);
    try {
      const r = await api.post("/ai/auto-entry", { message: "Catat bon/struk ini sebagai beban.", execute: false, image_base64: b64 });
      setLastImage(b64);
      setAutoPreview(r.data);
      const data = r.data.data || {};
      const detail = r.data.action === "expense"
        ? `Vendor: ${data.vendor || "-"}\nTanggal: ${data.date || "-"}\nTotal: Rp ${(data.amount || 0).toLocaleString("id-ID")}\nKategori: ${data.category || "-"} (${data.expense_account_code || "6000"})`
        : (r.data.confirmation_message || "Tidak jelas");
      setMessages((m) => [...m, { role: "assistant", id: `scan-r-${Date.now()}`, content: `📷 Hasil pindai bon:\n\n${detail}\n\nKlik ✓ Konfirmasi untuk pos jurnal.` }]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal scan bon"); }
    finally { setLoading(false); }
  };

  const analyzeDocFile = async (file) => {
    if (!file) return;
    const ok = /\.(xlsx|xls|csv|docx)$/i.test(file.name);
    if (!ok) { toast.error("Format tidak didukung. .xlsx/.xls/.csv/.docx"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Ukuran maks 10MB"); return; }
    setLoading(true);
    setMessages((m) => [...m, { role: "user", id: `doc-${Date.now()}`, content: `📎 Unggah: ${file.name} — minta AI buat penjurnalan.` }]);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("instruction", input || "Buat penjurnalan sesuai isi tabel. Pakai master data (partner/produk/akun) yang tersedia.");
      fd.append("execute", "false");
      const token = localStorage.getItem("nusa_token");
      const resp = await fetch(`${API}/ai/analyze-file`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
      const r = await resp.json();
      if (!resp.ok || !r.parsed) throw new Error(r.detail || r.error || "Gagal baca file");
      setAutoPreview({ ...r, bulk: true });
      const summary = `📎 Hasil baca ${r.source}: **${r.count}** aksi dapat dibuat.\n\n${(r.actions || []).slice(0, 5).map((a, i) => `${i + 1}. **${a.action}** — ${a.confirmation_message || ""}`).join("\n")}${r.count > 5 ? `\n...+${r.count - 5} lagi` : ""}\n\nKlik ✓ Eksekusi Semua untuk posting.`;
      setMessages((m) => [...m, { role: "assistant", id: `doc-r-${Date.now()}`, content: summary }]);
      setInput("");
    } catch (e) { toast.error(e.message || "Gagal baca file"); }
    finally { setLoading(false); }
  };

  const executeBulkActions = async () => {
    if (!autoPreview?.bulk) return;
    setLoading(true);
    try {
      const r = await api.post("/ai/analyze-file-execute", { actions: autoPreview.actions || [] });
      const d = r.data;
      toast.success(`${d.executed?.length || 0} aksi dieksekusi${d.errors?.length ? `, ${d.errors.length} gagal` : ""}`);
      setMessages((m) => [...m, { role: "assistant", id: `bulk-r-${Date.now()}`, content: `✅ ${d.executed?.length || 0} aksi tersimpan${d.errors?.length ? `; ${d.errors.length} baris gagal` : ""}.` }]);
      setAutoPreview(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal eksekusi"); }
    finally { setLoading(false); }
  };

  const resetChat = () => {
    setSessionId(null);
    setMessages([]);
    setAutoPreview(null);
    setLastImage(null);
    setGreeted(false);
    resetIdleTimer();
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="floating-ai-open-btn"
          className="fixed bottom-5 right-5 z-50 h-14 w-14 rounded-full bg-slate-900 text-amber-400 shadow-xl shadow-slate-900/30 hover:scale-105 active:scale-95 transition-transform flex items-center justify-center ring-4 ring-amber-500/20"
          title="AI Bookkeeping — tanya saya apa saja"
          aria-label="Buka AI Bookkeeping"
        >
          <Robot size={26} weight="duotone" />
          {unread > 0 && (
            <span data-testid="floating-ai-unread" className="absolute -top-1 -right-1 bg-rose-600 text-white text-[10px] font-semibold rounded-full min-w-[20px] h-5 px-1 flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="floating-ai-panel"
          className="fixed bottom-5 right-5 z-50 w-[calc(100vw-2rem)] sm:w-[380px] h-[560px] max-h-[calc(100vh-2.5rem)] bg-white border border-slate-200 rounded-xl shadow-2xl shadow-slate-900/20 flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2.5 bg-slate-900 text-white">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center shrink-0">
                <Robot size={18} weight="duotone" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold leading-tight">AI Bookkeeping</div>
                <div className="text-[10px] text-slate-300 leading-tight truncate" data-testid="floating-ai-model">{modelLabel}</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={resetChat} data-testid="floating-ai-reset" className="p-1.5 rounded hover:bg-white/10" title="Chat baru"><Trash size={14} /></button>
              <button onClick={() => setOpen(false)} data-testid="floating-ai-minimize" className="p-1.5 rounded hover:bg-white/10" title="Minimize"><Minus size={14} /></button>
              <button onClick={() => { setOpen(false); resetChat(); }} data-testid="floating-ai-close" className="p-1.5 rounded hover:bg-white/10" title="Tutup"><X size={14} /></button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-auto p-3 bg-slate-50 space-y-3" data-testid="floating-ai-messages">
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center px-4" data-testid="floating-ai-welcome">
                <div className="w-14 h-14 rounded-full bg-slate-900 text-amber-500 flex items-center justify-center mb-3">
                  <Robot size={28} weight="duotone" />
                </div>
                <div className="text-sm font-semibold text-slate-900">Halo, saya AI Bookkeeping</div>
                <div className="text-xs text-slate-500 mt-1">Tanya tentang keuangan, laba-rugi, atau minta saya mencatat transaksi.</div>
                <div className="mt-4 w-full grid grid-cols-1 gap-1.5 text-left text-xs">
                  {["Berapa laba bersih bulan ini?", "Catat beban sewa Rp 5 juta", "Jelaskan DPP Nilai Lain"].map((q) => (
                    <button key={q} onClick={() => setInput(q)} className="px-3 py-2 bg-white border border-slate-200 rounded-md hover:border-amber-400 hover:bg-amber-50 transition-colors" data-testid={`floating-ai-suggest-${q.slice(0, 10)}`}>{q}</button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((m, i) => {
                const parsed = m.role === "assistant" ? extractActions(m.content) : { text: m.content, actions: [] };
                return (
                <div key={m.id || i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  {m.role === "assistant" && (
                    <div className="w-7 h-7 rounded-full bg-slate-900 text-amber-500 flex items-center justify-center shrink-0">
                      <Robot size={14} weight="duotone" />
                    </div>
                  )}
                  <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-800"}`}>
                    {m.image_url && <img src={m.image_url} alt="bon" className="max-w-[200px] rounded border border-slate-300 mb-1.5" />}
                    {parsed.text || (m.streaming ? "…" : "")}
                    {parsed.actions.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-slate-200 space-y-1.5">
                        {parsed.actions.map((a, k) => (
                          <button
                            key={k}
                            data-testid={`ai-action-${a.action}-${k}`}
                            onClick={() => { navigate(ACTION_ROUTES[a.action], { state: { prefillAction: a.action, prefill: a.data || {} } }); setOpen(false); toast.info("Form dibuka dengan isian dari AI — periksa sebelum simpan"); }}
                            className="w-full flex items-center justify-between gap-2 px-3 py-2 text-xs font-medium rounded-md bg-amber-600 text-white hover:bg-amber-700 transition-colors"
                          >
                            <span>{a.label || ACTION_LABELS[a.action]}</span>
                            <ArrowSquareOut size={13} weight="bold" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                );
              })
            )}
            <div ref={bottomRef} />
          </div>

          {/* Composer */}
          <div className="border-t border-slate-200 p-2.5 bg-white space-y-2">
            <div className="flex items-center gap-2 text-[11px]">
              <label className="flex items-center gap-1.5 cursor-pointer select-none" data-testid="floating-ai-auto-toggle">
                <input type="checkbox" checked={autoEntry} onChange={(e) => { setAutoEntry(e.target.checked); setAutoPreview(null); }} className="h-3 w-3" />
                <Lightning size={12} weight={autoEntry ? "fill" : "regular"} className={autoEntry ? "text-amber-500" : "text-slate-400"} />
                <span className={autoEntry ? "text-amber-700 font-medium" : "text-slate-600"}>Auto-Entry</span>
              </label>
              {autoPreview && autoPreview.action !== "unknown" && (
                <>
                <Button size="sm" onClick={executeAutoEntry} disabled={loading} className="ml-auto bg-emerald-600 hover:bg-emerald-700 h-6 text-[11px] px-2" data-testid="floating-ai-auto-confirm">✓ Konfirmasi</Button>
                {ACTION_ROUTES[autoPreview.action] && (
                  <Button size="sm" variant="outline" disabled={loading}
                    onClick={() => { navigate(ACTION_ROUTES[autoPreview.action], { state: { prefillAction: autoPreview.action, prefill: autoPreview.data || {} } }); setOpen(false); toast.info("Form dibuka dengan isian dari AI — periksa sebelum simpan"); }}
                    className="h-6 text-[11px] px-2" data-testid="floating-ai-open-form">↗ Buka Form</Button>
                )}
                </>
              )}
            </div>
            <div className="flex gap-1.5">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) scanReceipt(f); e.target.value = ""; }}
                data-testid="floating-ai-scan-input"
              />
              <input
                ref={docInputRef}
                type="file"
                accept=".xlsx,.xls,.csv,.docx"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) analyzeDocFile(f); e.target.value = ""; }}
                data-testid="floating-ai-doc-input"
              />
              <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={loading} className="h-9 w-9 p-0 shrink-0 border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid="floating-ai-scan-btn" title="Scan bon/struk (gambar)">
                <Camera size={16} weight="duotone" />
              </Button>
              <Button type="button" variant="outline" onClick={() => docInputRef.current?.click()} disabled={loading} className="h-9 w-9 p-0 shrink-0 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="floating-ai-doc-btn" title="Unggah Excel/CSV/Word untuk penjurnalan massal">
                <Paperclip size={16} weight="duotone" />
              </Button>
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={autoEntry ? "Jual 5 pcs ke PT ABC Rp 500rb" : "Tanya atau tekan 📷"}
                className="h-9 text-sm"
                disabled={loading}
                data-testid="floating-ai-input"
              />
              <Button onClick={send} disabled={loading || !input.trim()} className="h-9 px-3 bg-amber-600 hover:bg-amber-700 shrink-0" data-testid="floating-ai-send-btn">
                <PaperPlaneRight size={14} weight="fill" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
